// conciertos.js — las fechas del artista que suena.
//
// Contrato: `ARTISTAS_WEB_INSTRUCCIONES.md` §7 (servidor, 26.08.26).
// Endpoint: https://listen.astra.fm/conciertos/<slug>
//
// Los datos vienen de JamBase a través de nuestro servidor, que los consulta y
// los cachea. **La web nunca llama a JamBase.**
//
// ⚠️ Cuatro cosas del contrato, y las dos primeras son condiciones de uso:
//
//   1. **Atribución obligatoria.** Es condición de la licencia de datos: el
//      bloque tiene que enseñar el enlace que viene en `attribution`. Por eso
//      este servicio lo devuelve siempre y no lo esconde.
//   2. **Solo al pintar la ficha de un artista.** Detrás hay una cuota mensual
//      de proveedor, así que nada de precargar listados enteros.
//   3. `404` (slug desconocido) y `503` (sin datos) **no son errores**: son la
//      respuesta normal para un artista sin gira. No se pinta el bloque y ya.
//   4. `stale: true` significa «esto es de hace más de un día» —cuota agotada o
//      proveedor caído— y aun así se pinta: son las últimas fechas conocidas.
import { artistSlug } from "@/services/editorialArtists";
import { LISTEN } from "./host";

const BASE = `${LISTEN}`;
const TTL = 5 * 60 * 1000;

// Por artista, no global: cada ficha pide el suyo.
const cache = new Map();

// Se exporta porque `HeroAgenda` parte la fecha en día y mes para su celda, y
// dos tablas de meses son dos sitios donde equivocarse.
export const MESES = [
  "ENE", "FEB", "MAR", "ABR", "MAY", "JUN",
  "JUL", "AGO", "SEP", "OCT", "NOV", "DIC",
];

/** «2026-10-07» → «07 OCT», que es como lo escribe la maqueta. */
function enCorto(fecha) {
  const [y, m, d] = (fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]}`;
}

/** «Teatro Flores, Buenos Aires». Se salta lo que venga `null`. */
function donde(evento) {
  return [evento.venue, evento.city].filter(Boolean).join(", ");
}

function normaliza(evento) {
  return {
    date: evento.date || "",
    dateShort: enCorto(evento.date),
    venue: evento.venue || "",
    city: evento.city || "",
    country: evento.country || "",
    where: donde(evento),
    // `false` cuando no lo es; si lo es, trae el nombre del festival.
    festival: evento.festival || "",
    url: evento.url || "",
    tickets: evento.tickets || "",
  };
}

/**
 * Las fechas del artista, o `null` si no hay ninguna.
 *
 * `null` es la respuesta normal: la mayoría del catálogo no tiene gira activa.
 * Quien lo llame no debe pintar el bloque en ese caso.
 *
 * @returns {Promise<{next, rest, more, attribution, stale}|null>}
 */
export async function getConciertos(artistName) {
  const slug = artistSlug(artistName || "");
  if (!slug) return null;

  const guardado = cache.get(slug);
  if (guardado && Date.now() - guardado.at < TTL) return guardado.data;

  let data = null;

  try {
    const r = await fetch(`${BASE}/conciertos/${encodeURIComponent(slug)}`);

    // 404 y 503 son respuestas previstas, no fallos: el artista no está o no
    // hay datos ahora mismo. En los dos casos, no hay bloque.
    if (r.ok) {
      const json = await r.json();
      const eventos = (json?.data?.events || []).map(normaliza);

      if (eventos.length) {
        const [proximo, ...resto] = eventos;
        data = {
          next: proximo,
          rest: resto,
          // «+ 2 fechas anunciadas». Se omite con una sola fecha: «+ 0» es peor
          // que no decir nada.
          more: resto.length
            ? `+ ${resto.length} ${resto.length === 1 ? "fecha anunciada" : "fechas anunciadas"}`
            : "",
          // Obligatoria por licencia. Ver arriba.
          attribution: json?.data?.attribution || null,
          stale: Boolean(json?.data?.stale),
        };
      }
    }
  } catch (error) {
    // Red caída. Como el 404: no hay bloque, y la ficha sigue entera.
  }

  cache.set(slug, { at: Date.now(), data });
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// La agenda de la portada
// ═══════════════════════════════════════════════════════════════════════════
//
// Contrato: `CONCIERTOS_WEB_INSTRUCCIONES.md` (servidor, 01.09.26).
// Endpoint: `https://listen.astra.fm/conciertos.json`, `max-age=0, must-revalidate` (304 por ETag; medido 05.09.26).
//
// 🔴 **Sustituye a JamBase, que se desactivó por coste.** Nada de esto viene de
// un proveedor: son tres afluentes propios —lo que proponen los artistas
// verificados, las fechas de lo publicado en Radar y una búsqueda mensual con
// IA— y **los tres pasan por la redacción** antes de existir aquí. Por eso esta
// agenda no lleva atribución y `getConciertos` sí: aquella es de licencia
// ajena, esta es nuestra.
//
// Lo que el servidor ya garantiza, y que por tanto **aquí no se vuelve a
// hacer**:
//
//   · Solo de hoy en adelante, en orden. Lo pasado desaparece solo, así que
//     filtrar por fecha aquí sería repetir su trabajo y arriesgarse a discrepar
//     con él por una zona horaria.
//   · Un máximo de **N fechas por artista** y 200 filas. **N lo ajusta la
//     redacción** —hoy 3, y el contrato consolidado deja el rango en 1..10—, así
//     que la web no puede contar con que sean tres: por eso se agrupa aquí abajo
//     en vez de confiar en que el tope sea pequeño.
//   · `fecha` siempre `YYYY-MM-DD` sin hora.
//
// Y dos cosas que el contrato consolidado añade y que **aquí no se usan**, pero
// conviene que estén escritas:
//
//   · `lat` y `lon` traen la ciudad del evento geocodificada, o `null` mientras
//     el servidor no la ha resuelto. Con ellas se puede ofrecer «conciertos cerca
//     de ti» —geolocalización más distancia haversine—, que es una sección que no
//     existe en Figma y por tanto no se compone.
//   · `festivales` es la clave hermana. Tampoco tiene nodo: dónde y cómo pintarlos
//     es decisión de diseño, y hasta que la haya no se lee.
const URL_AGENDA = `${BASE}/conciertos.json`;

// Los vendedores que tienen botón propio en la agenda. El resto, o ninguno, lleva
// el `cta` de contorno. Lista cerrada: es lo que se pidió al servidor.
const VENDEDORES = new Set(["ticketmaster"]);

// Los mismos cinco minutos que manda el servidor.
const TTL_AGENDA = 5 * 60 * 1000;

let agendaCache = null;
let agendaAt = 0;

/** «2026-09-12» → «12 SEP 2026», que es como lo escribe la franja del hero. */
function enFranja(fecha) {
  const [y, m, d] = String(fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]} ${y}`;
}

/**
 * La agenda de la portada, ya lista para pintar.
 *
 * 🔴 **Un paso por artista, no por fecha**, que es lo que pide §5 del contrato:
 * «en la home queremos una tarjeta por artista con sus fechas dentro; agrupad por
 * `slug`, y con `slug: null` por `artista`». El JSON es plano —una fila por
 * concierto— y el tope por artista lo mueve la redacción entre 1 y 10, así que
 * sin agrupar un carrusel de cinco pasos podría ser un solo artista.
 *
 * Cada artista entra una vez, con su fecha más próxima, en el orden en que el
 * servidor los mandó, que ya es el de lo más inminente primero. `fechas` dice
 * cuántas tiene en total: en el hero no cabe la lista entera, pero sí decir que
 * esta es una de tres.
 *
 * Nunca lanza: sin agenda la portada se pinta igual, con el hero entero para la
 * emisión.
 *
 * @returns {Promise<Array<{artista, slug, fecha, cuando, donde, entradas, vendedor, imagen, cartel, fechas}>>}
 */
export async function getAgenda() {
  const ahora = Date.now();
  if (agendaCache && ahora - agendaAt <= TTL_AGENDA) return agendaCache;

  let lista = [];

  try {
    const r = await fetch(URL_AGENDA, { headers: { Accept: "application/json" } });
    if (r.ok) {
      const json = await r.json();
      const filas = Array.isArray(json?.conciertos) ? json.conciertos : [];
      const vistos = new Map();

      for (const c of filas) {
        // Sin artista y sin fecha no hay nada que enseñar.
        if (!c?.artista || !c?.fecha) continue;

        // 🔴 **Sin cartel, la fecha no entra en el carrusel.** Pablo,
        // 21.09.26: «la imagen de los conciertos no tiene nada que ver con la
        // ficha del artista, es solo para que alimente el carrusel». El módulo
        // enseña un cartel; una fecha que no lo tiene no tiene nada que enseñar
        // ahí, y **el retrato del artista no la sustituye**.
        //
        // ⚠️ Esto solo afecta al carrusel. La lista de conciertos de Radio va
        // por `getConciertos()` y no pasa por aquí: allí una fecha sin cartel se
        // sigue viendo, porque allí lo que se enseña es la fecha.
        if (!c?.cartel) continue;

        // Por `slug`, y por nombre cuando no lo hay: un evento `directo` de un
        // artista de fuera del catálogo llega sin slug y no puede caer en el
        // mismo saco que los demás sin slug.
        const clave = c.slug || c.artista;
        if (vistos.has(clave)) {
          // Las demás fechas no se pintan, pero sí se cuentan: es la diferencia
          // entre «toca el 12» y «tiene gira».
          vistos.get(clave).fechas += 1;
          continue;
        }

        vistos.set(clave, {
          artista: c.artista,
          slug: c.slug || "",
          fecha: c.fecha,
          cuando: enFranja(c.fecha),
          // Texto final del servidor: la ciudad puede traer el país entre
          // paréntesis y **no se parsea**.
          donde: [c.sala, c.ciudad].filter(Boolean).join(" · "),
          entradas: c.entradas || "",
          // Quién vende las entradas, y con ello qué botón lleva la celda de
          // comprar: `Brand/Ticketmaster` o el `cta` de contorno. Contrato
          // `CONCIERTOS_WEB_INSTRUCCIONES.md` §2, desde el 27.09.26:
          // `"ticketmaster"` o `null`, lista cerrada; lo marca la redacción en
          // el Studio y las fechas de Radar llegan siempre con `null`.
          //
          // 🔴 **No se deduce del enlace.** Los de Ticketmaster llegan por un
          // afiliado (`ticketmaster.evyy.net`), no por `ticketmaster.es`, y una
          // sala puede vender por Ticketmaster desde su web. Un valor que no
          // conozcamos lleva el contorno, como pide el contrato. Sin enlace no
          // hay vendedor que valga.
          vendedor: c.entradas && VENDEDORES.has(c.vendedor) ? c.vendedor : "",

          // 🔴 **El carrusel se alimenta de `cartel`, y de nada más.**
          //
          // `cartel` es la imagen de **ese concierto**: la sube Pablo desde la
          // sección de conciertos de la administración, vive en
          // `/conciertos/images/`, mide 2508 × 1200 y caduca con la fecha.
          //
          // ⚠️ **`imagen` ya no existe en este contrato.** Lo dijo Pablo tres
          // veces —la última el 21.09.26— y desde el 22.09.26 **lo dice el
          // contrato**: «en los conciertos ya no se envía `imagen`… si un
          // concierto no tiene cartel, llega `null` y decidís qué pintar, pero
          // nunca la foto del artista». Comprobado en el feed: llega `null` en
          // las siete fechas.
          //
          // Estuvo aquí como «respaldo» de las fechas sin cartel y eso es lo que
          // hacía que el carrusel enseñara artistas en vez de conciertos. Lo que
          // decidimos pintar es nada: una fecha sin cartel ya no llega hasta
          // aquí.
          cartel: c.cartel,
          fechas: 1,
        });
      }

      lista = [...vistos.values()];
    }
  } catch (error) {
    // Red caída. Como una agenda vacía: la portada se pinta igual.
  }

  agendaCache = lista;
  agendaAt = ahora;
  return lista;
}
